import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { IS_PAID_USER } from 'reactApp/appHelpers/configHelpers';
import {
    preOverquotaBannerConfig,
    preOverquotaBannerIgnoreShowLimits,
    preOverquotaExpGroup,
} from 'reactApp/appHelpers/featuresHelpers/features/overquotaPre';
import { PaymentUTM } from 'reactApp/constants/paymentUTM';
import { getAccountDwhChains } from 'reactApp/modules/dwh/dwh.helpers';
import { storeHelper } from 'reactApp/modules/promo/promo.helpers';
import { historyPush } from 'reactApp/modules/router/router.module';
import { getQuotaPromoUrl } from 'reactApp/sections/QuotaLanding/QuotaLanding.data';
import { sendDwh } from 'reactApp/utils/ga';
import { getPlatformInfo } from 'reactApp/utils/gaHelpers';
import opener from 'reactApp/utils/opener';

export const PRE_OVERQUOTA_BANNER_ID = 'preoverquota-banner';

export const sendPreOverquotaDwhBanner = ({
    action,
    mode,
}: {
    action: 'show' | 'click' | 'click-cleaner' | 'click-other' | 'close';
    mode?: 'landing' | 'paywall' | 'cleaner' | 'none';
}) => {
    const { dwhData: accountDwhData, dwhChains: accountDwhChains } =
        action === 'show' ? getAccountDwhChains(['name']) : { dwhData: {}, dwhChains: [] };

    const dwhData = {
        name: 'mnz_aon_preoverquota',
        paid_user: IS_PAID_USER,
        platform: getPlatformInfo().join('_'),
        group: preOverquotaExpGroup,
        mode,
        ...accountDwhData,
    };

    const baseDwhChains: (keyof typeof dwhData)[][] = [['name', 'paid_user', 'platform']];
    const expDwhChains: (keyof typeof dwhData)[][] = preOverquotaExpGroup ? [['name', 'group', 'platform']] : [];
    const dwh_enabled_fields = [...baseDwhChains, ...expDwhChains, ...accountDwhChains];

    sendDwh({
        eventCategory: 'banner',
        action,
        dwh_enabled: true,
        dwh_enabled_fields,
        dwhData,
    });
};

export const isPreOverquotaBannerPeriodPassedCheck = () => {
    return {
        check:
            preOverquotaBannerIgnoreShowLimits ||
            !storeHelper.getValue(PRE_OVERQUOTA_BANNER_ID) ||
            storeHelper.isPassed(PRE_OVERQUOTA_BANNER_ID, { daysCount: preOverquotaBannerConfig.period }),
        lasTime: new Date(storeHelper.getLastTime(PRE_OVERQUOTA_BANNER_ID)).toLocaleString(),
        now: new Date().toLocaleString(),
        preOverquotaBannerIgnoreShowLimits,
        preOverquotaBannerPeriod: preOverquotaBannerConfig.period,
        PRE_OVERQUOTA_BANNER_ID,
    };
};

export const usePreOverquotaBannerCallbacks = ({ hide }: { hide: () => void }) => {
    const dispatch = useDispatch();

    const onShowHandler = useCallback(() => {
        sendPreOverquotaDwhBanner({ action: 'show' });
    }, []);

    const onPrimaryHandler = useCallback(() => {
        sendPreOverquotaDwhBanner({ action: 'click', mode: preOverquotaBannerConfig.primaryButton?.mode });

        if (preOverquotaBannerConfig.primaryButton?.mode === 'landing') {
            dispatch(
                historyPush({
                    id: '/promo/quota',
                    search: `?${PaymentUTM.preOverquotaBanner}`,
                    hash: 'tariffs',
                })
            );
            return;
        }
    }, []);

    const onSecondaryHandler = useCallback(() => {
        if (preOverquotaBannerConfig.secondButton?.enable && preOverquotaBannerConfig.secondButton?.mode === 'cleaner') {
            sendPreOverquotaDwhBanner({ action: 'click-cleaner' });
            opener(
                getQuotaPromoUrl({
                    query: `?${PaymentUTM.preOverquotaBannerCleaner}`,
                    hash: 'cleaner',
                })
            );
            return;
        }
        sendPreOverquotaDwhBanner({ action: 'click-other', mode: preOverquotaBannerConfig.secondButton?.mode });
        storeHelper.markAsShown(PRE_OVERQUOTA_BANNER_ID);
        hide();
    }, []);

    const onCancelHandler = useCallback(() => {
        sendPreOverquotaDwhBanner({ action: 'close' });
        storeHelper.markAsShown(PRE_OVERQUOTA_BANNER_ID);
        hide();
    }, []);

    return { onShowHandler, onPrimaryHandler, onSecondaryHandler, onCancelHandler };
};
